import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { sendMessage } from "../../api/slack";
import { useAuthStore } from "../../authProvider";
import { useLocation } from "react-router-dom";

// Define a type for allowed values in the data record
type DataValue = string | number | boolean | null | DataObject | DataArray;
type DataObject = { [key: string]: DataValue };
type DataArray = DataValue[];

interface EmergencyMessageProps {
  buttonText?: string;
  buttonColor?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  buttonVariant?: "text" | "outlined" | "contained";
  dialogTitle?: string;
  dialogDescription?: string;
}

/**
 * EmergencyMessage component for sending urgent messages to Slack
 *
 * This component renders a button that opens a dialog where the user can
 * enter a custom message. The message will be sent to Slack along with
 * any additional data provided as props.
 */
const EmergencyMessage: React.FC<EmergencyMessageProps> = ({
  buttonText = "Send emergency message",
  buttonColor = "error",
  buttonVariant = "contained",
  dialogTitle = "Send emergency message",
  dialogDescription = "Enter a message to send to the support channel. The development team will receive an immediate notification.",
}) => {
  const location = useLocation();
  const { user } = useAuthStore();

  // State for dialog visibility
  const [open, setOpen] = useState(false);
  // State for the custom message
  const [message, setMessage] = useState("");
  // Loading state during API call
  const [loading, setLoading] = useState(false);
  // Notification states
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Function to format the data as text
  const formatDataAsText = (data: Record<string, unknown>): string => {
    // Default formatter: convert JSON to formatted text
    let formattedText = "```\nAdditional data:\n";

    Object.entries(data).forEach(([key, value]) => {
      // Handle different types of values
      if (!value) {
        return;
      }

      let displayValue = value;

      if (typeof value === "object" && value) {
        try {
          displayValue = JSON.stringify(value, null, 2);
        } catch (e) {
          displayValue = "[Complex object]";
        }
      }

      formattedText += `${key}: ${displayValue}\n`;
    });

    formattedText += "```";
    return formattedText;
  };

  // Handle dialog open
  const handleClickOpen = () => {
    setOpen(true);
  };

  // Handle dialog close
  const handleClose = () => {
    setOpen(false);
  };

  // Handle message submission
  const handleSubmit = async () => {
    // Don't send if message is empty
    if (!message.trim()) {
      return;
    }

    setLoading(true);

    try {
      const additionalData = {
        user: {
          id: user?.id,
          name: user?.name,
          email: user?.email,
          role: user?.role,
        },
        location: location,
      };

      // Prepare the full message with the additional data
      const additionalDataText =
        Object.keys(additionalData).length > 0
          ? "\n\n" + formatDataAsText(additionalData)
          : "";

      const fullMessage = `${message}${additionalDataText}`;

      // Send the message to Slack
      await sendMessage({
        text: fullMessage,
      });

      // Show success notification
      setShowSuccess(true);
      // Reset the form and close the dialog
      setMessage("");
      setOpen(false);
    } catch (error) {
      // Show error notification
      setErrorMessage(
        error instanceof Error ? error.message : "An error occurred"
      );
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        color={buttonColor}
        onClick={handleClickOpen}
      >
        {buttonText}
      </Button>

      {/* Emergency Message Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialogDescription}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="emergency-message"
            label="Message"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            variant="outlined"
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={loading || !message.trim()}
            startIcon={loading ? <CircularProgress size={24} /> : null}
          >
            {loading ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success notification */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          Message sent successfully!
        </Alert>
      </Snackbar>

      {/* Error notification */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Error during sending: {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmergencyMessage;
